/**
 * Widget view-option placeholder mechanism.
 *
 * Maintains a registry of placeholder resolvers and substitutes `{{token}}` patterns
 * in view-option string values at widget render time. Resolution is synchronous.
 *
 * Seeds the v1 catalogue (`{{dashboard.title}}`, `{{dashboard.description}}`, `{{date.today}}`)
 * via `providePlaceholderCatalogue()` registered as an `APP_INITIALIZER`.
 */

import { APP_INITIALIZER, Injectable, Provider } from '@angular/core';
import { DateTools } from '@sol/components';
import { DashboardDetail } from '../models/dashboard-management';

/**
 * Context passed to each resolver. Carries the current dashboard domain object.
 * Resolution is performed inside the consuming widget's state class (not in the
 * view pipeline or the schema-to-form adapter).
 */
export interface PlaceholderContext {
  readonly dashboard: DashboardDetail;
}

/**
 * Synchronous resolver function: given a context, returns a string.
 * MUST NOT throw — errors are caught and the literal token is preserved.
 */
export type PlaceholderResolver = (ctx: PlaceholderContext) => string;

/** Matches `{{token}}` where token starts with a lowercase letter and may contain lowercase, digits, underscores, dots. */
const PLACEHOLDER_REGEX = /\{\{[a-z][a-z0-9_.]*\}\}/g;

@Injectable({ providedIn: 'root' })
export class PlaceholderService {
  private readonly resolvers = new Map<string, PlaceholderResolver>();
  /** Deduplication set for console.warn — one warn per unique unrecognised token per service instance. */
  private readonly warnedTokens = new Set<string>();

  public register(id: string, resolver: PlaceholderResolver): void {
    this.resolvers.set(id, resolver);
  }

  /**
   * Scan `value` for `{{token}}` patterns and replace each with the resolved string.
   *
   * - Unrecognised token: leave literal, emit `console.warn` once per token per service instance.
   * - Resolver throws: catch, leave literal, warn.
   * - `ctx.dashboard` missing or resolver error: same treatment.
   */
  public resolve(value: string, ctx: PlaceholderContext): string {
    if (!value || !value.includes('{{')) {
      return value;
    }

    return value.replace(PLACEHOLDER_REGEX, match => {
      const resolver = this.resolvers.get(match);
      if (!resolver) {
        if (!this.warnedTokens.has(match)) {
          this.warnedTokens.add(match);
          console.warn(`[PlaceholderService] Unrecognised placeholder token: ${match}`);
        }
        return match;
      }

      try {
        return resolver(ctx);
      } catch (err) {
        if (!this.warnedTokens.has(match)) {
          this.warnedTokens.add(match);
          console.warn(`[PlaceholderService] Resolver for ${match} threw:`, err);
        }
        return match;
      }
    });
  }
}

/**
 * Seeds the v1 placeholder catalogue with the built-in dashboard + date tokens.
 * Registered as an `APP_INITIALIZER` so the catalogue is ready before any widget renders.
 * Exported for unit testing — the catalogue is otherwise wired only via the provider below.
 */
export function seedPlaceholderCatalogue(service: PlaceholderService): () => void {
  return () => {
    service.register('{{dashboard.title}}', ctx => ctx.dashboard.name);
    service.register('{{dashboard.description}}', ctx => ctx.dashboard.description);
    // Today's date as dd-MM-yyyy in local time (Angular formatDate via DateTools).
    service.register('{{date.today}}', () => DateTools.format(DateTools.now(), 'dd-MM-yyyy') ?? '');
  };
}

export function providePlaceholderCatalogue(): Provider {
  return {
    provide: APP_INITIALIZER,
    useFactory: seedPlaceholderCatalogue,
    deps: [PlaceholderService],
    multi: true,
  };
}
